// packages/ui/src/components/security/SecurityActivityLog.tsx
//
// Wave 12 Task 104 — Activity Log table per design PNG.

import type { JSX } from 'react';
import { cn } from '@printway-global/design-system';
import type { SecurityActivityEntry } from './SecurityCenterPage.js';

export interface SecurityActivityLogProps {
  entries: SecurityActivityEntry[];
}

const STATUS_BADGE: Record<SecurityActivityEntry['status'], string> = {
  success: 'bg-green-100 text-green-700',
  failed: 'bg-yellow-100 text-yellow-700',
  blocked: 'bg-red-100 text-red-700',
};

const STATUS_LABEL: Record<SecurityActivityEntry['status'], string> = {
  success: 'Success',
  failed: 'Failed',
  blocked: 'Blocked',
};

/**
 * Format ISO 8601 timestamp to client locale display. Falls back to raw
 * string if input isn't a valid ISO timestamp (defensive for legacy data
 * or partial deploys).
 */
function formatTimestamp(raw: string): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString();
}

export function SecurityActivityLog({
  entries,
}: SecurityActivityLogProps): JSX.Element {
  if (entries.length === 0) {
    return (
      <p className="text-center text-sm text-pw-text-muted">
        No recent activity recorded.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="border-b border-pw-border text-xs font-medium uppercase tracking-wider text-pw-text-muted">
          <tr>
            <th className="pb-3 pr-4">Timestamp</th>
            <th className="pb-3 pr-4">User</th>
            <th className="pb-3 pr-4">Action</th>
            <th className="pb-3 pr-4">IP Address</th>
            <th className="pb-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-pw-border">
          {entries.map((e, i) => (
            <tr key={`${e.timestamp}-${i}`}>
              <td className="py-3 pr-4 font-mono text-xs text-pw-text-muted">
                {formatTimestamp(e.timestamp)}
              </td>
              <td className="py-3 pr-4 text-pw-text">{e.user}</td>
              <td className="py-3 pr-4 font-mono text-xs text-pw-text">
                {e.action}
              </td>
              <td className="py-3 pr-4 font-mono text-xs text-pw-text-muted">
                {e.ipAddress}
              </td>
              <td className="py-3">
                <span
                  className={cn(
                    'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                    STATUS_BADGE[e.status],
                  )}
                >
                  {STATUS_LABEL[e.status]}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SecurityActivityLog;
